import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/drill/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import TimeScroller from "@/components/ui/time-scroller";
import { Check, ChevronsUpDown, Pencil, Trash2 } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { isAtLeast } from "@/lib/roles";

const toMinutes = (value) => {
  if (!value) return null;
  const [h, m] = String(value).split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

const todayKey = () => new Date().toISOString().slice(0, 10);

const logDateKey = (log) => {
  const raw = log?.log_date || log?.date || log?.created_date || log?.created_at;
  return raw ? String(raw).slice(0, 10) : todayKey();
};

const overlapsByMinute = (aStart, aEnd, bStart, bEnd) => {
  const aS = toMinutes(aStart), aE = toMinutes(aEnd), bS = toMinutes(bStart), bE = toMinutes(bEnd);
  if ([aS, aE, bS, bE].some((v) => v == null)) return false;
  return Math.min(aE, bE) - Math.max(aS, bS) >= 1;
};

const formatHour = (hour) => `${String(hour % 24).padStart(2, "0")}:00`;
const userLabel = (u) => u?.full_name || u?.name || u?.email || "Unnamed rig";
const identifiersForUser = (u) => new Set([u?.id, u?.rig_id, u?.full_name, u?.name].filter(Boolean).map(String));

function TaskPicker({ tasks, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = tasks.find((t) => String(t.id) === String(value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="mt-1 h-11 w-full justify-between rounded-xl font-normal">
          <span className="min-w-0 text-left">
            {selected ? (
              <span className="block min-w-0">
                <span className="block truncate text-sm font-medium leading-tight">{selected.name}</span>
                {selected.description && <span className="block truncate text-xs leading-tight text-slate-500 dark:text-slate-400">{selected.description}</span>}
              </span>
            ) : <span className="text-slate-500">Select task</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] rounded-xl p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search tasks..." autoFocus />
          <CommandList className="max-h-72">
            <CommandEmpty>No matching tasks.</CommandEmpty>
            {tasks.map((task) => (
              <CommandItem
                key={task.id}
                value={`${task.name || ""} ${task.description || ""}`}
                onSelect={() => { onChange(String(task.id)); setOpen(false); }}
                className="items-center gap-2 px-3 py-1.5 min-h-0"
              >
                <div className="min-w-0 flex-1 flex items-baseline gap-2">
                  <div className="text-sm font-medium leading-tight truncate">{task.name}</div>
                  {task.description && <div className="min-w-0 truncate text-xs font-normal leading-tight text-slate-500 dark:text-slate-400">{task.description}</div>}
                </div>
                {String(value) === String(task.id) && <Check className="mt-0.5 h-4 w-4 shrink-0" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function DailyLog() {
  const { effectiveRole, user } = useAuth();
  const canManage = isAtLeast(effectiveRole, "admin");
  const isRig = String(effectiveRole || "rig").toLowerCase() === "rig";
  const canViewAssignedRigs = isAtLeast(effectiveRole, "supervisor");
  const canAdd = isRig || canViewAssignedRigs;
  const canEditLogs = isRig || canViewAssignedRigs;
  const [logs, setLogs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [rigUsers, setRigUsers] = useState([]);
  const [selectedRigId, setSelectedRigId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageMode, setManageMode] = useState("menu");
  const [editingLogId, setEditingLogId] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [finishTime, setFinishTime] = useState("");
  const [taskId, setTaskId] = useState("");
  const [comments, setComments] = useState("");
  const [message, setMessage] = useState("");
  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);

  const load = async () => {
    try {
      const requests = [
        base44.entities.DailyLog.list("-created_date"),
        base44.entities.DailyTask.list("name"),
        canViewAssignedRigs ? base44.entities.User.list() : Promise.resolve([]),
      ];
      const [daily, dailyTasks, users] = await Promise.all(requests);
      const today = todayKey();
      setLogs((daily || []).filter((log) => logDateKey(log) === today));
      setTasks(dailyTasks || []);

      if (canViewAssignedRigs) {
        const rigList = (users || []).filter((u) => {
          if (String(u.role || "").toLowerCase() !== "rig") return false;
          if (isAtLeast(effectiveRole, "admin")) return true;
          return String(u.supervisor_id || "") === String(user?.id || "");
        });
        setRigUsers(rigList);
        setSelectedRigId((current) => current && rigList.some((r) => String(r.id) === String(current)) ? current : (rigList[0]?.id || ""));
      }
    } catch (e) {
      console.error(e);
      setMessage(e?.message || "Unable to load Daily Log.");
    }
  };

  useEffect(() => { void load(); }, [effectiveRole, user?.id]);

  const selectedTask = useMemo(() => tasks.find((t) => String(t.id) === String(taskId)), [tasks, taskId]);
  const selectedRig = useMemo(() => rigUsers.find((r) => String(r.id) === String(selectedRigId)), [rigUsers, selectedRigId]);

  const visibleLogs = useMemo(() => {
    if (isRig) {
      const ids = identifiersForUser(user);
      return logs.filter((log) => {
        const candidates = [log.rig_user_id, log.rig_id, log.rig_name].filter(Boolean).map(String);
        return candidates.some((id) => ids.has(id));
      });
    }
    if (!selectedRigId) return [];
    const ids = identifiersForUser(selectedRig);
    return logs.filter((log) => {
      const candidates = [log.rig_user_id, log.rig_id, log.rig_name].filter(Boolean).map(String);
      return candidates.some((id) => ids.has(id));
    });
  }, [logs, isRig, user, selectedRigId, selectedRig]);

  const hasOverlap = useMemo(() => {
    if (!startTime || !finishTime) return false;
    return visibleLogs.some((r) => r.id !== editingLogId && overlapsByMinute(startTime, finishTime, r.start_time, r.end_time));
  }, [startTime, finishTime, visibleLogs, editingLogId]);

  const save = async () => {
    setMessage("");
    if (!isRig && !canViewAssignedRigs) return;
    if (!startTime || !finishTime || toMinutes(startTime) >= toMinutes(finishTime)) {
      setMessage("Enter a valid start and finish time.");
      return;
    }
    if (!taskId) {
      setMessage("Select a task.");
      return;
    }
    if (hasOverlap) {
      setMessage("Overlap detected. Separate tasks must not overlap by 1 minute or more.");
      return;
    }
    try {
      const payload = {
        start_time: startTime,
        end_time: finishTime,
        log_type: selectedTask?.name || null,
        task_id: selectedTask?.id || null,
        task_description: selectedTask?.description || null,
        comments: comments.trim() || null,
        log_date: todayKey(),
        rig_user_id: isRig ? (user?.id || null) : (selectedRig?.id || null),
        rig_id: isRig ? (user?.rig_id || user?.id || user?.full_name || user?.name || null) : (selectedRig?.rig_id || selectedRig?.id || selectedRig?.full_name || selectedRig?.name || null),
        rig_name: isRig ? userLabel(user) : userLabel(selectedRig),
      };
      if (editingLogId) await base44.entities.DailyLog.update(editingLogId, payload);
      else await base44.entities.DailyLog.create(payload);
      closeAdd();
      await load();
    } catch (e) {
      setMessage(e?.message || "Unable to save Daily Log entry.");
    }
  };

  const saveTask = async () => {
    setMessage("");
    if (!taskName.trim()) {
      setMessage("Enter a task name.");
      return;
    }
    try {
      if (editingTaskId) {
        await base44.entities.DailyTask.update(editingTaskId, { name: taskName.trim(), description: taskDescription.trim() });
      } else {
        await base44.entities.DailyTask.create({ name: taskName.trim(), description: taskDescription.trim() });
      }
      setTaskName("");
      setTaskDescription("");
      setEditingTaskId(null);
      setMessage("");
      setManageMode(editingTaskId ? "edit" : "create");
      await load();
    } catch (e) {
      setMessage(e?.message || "Unable to save task.");
    }
  };

  const deleteLog = async (log) => {
    if (!canEditLogs || !log?.id) return;
    if (!window.confirm("Delete this Daily Log entry?")) return;
    try {
      setMessage("");
      await base44.entities.DailyLog.delete(log.id);
      if (editingLogId === log.id) closeAdd();
      await load();
    } catch (e) {
      setMessage(e?.message || "Unable to delete Daily Log entry.");
    }
  };

  const closeAdd = () => {
    setAddOpen(false);
    setEditingLogId(null);
    setStartTime("");
    setFinishTime("");
    setTaskId("");
    setComments("");
    setMessage("");
  };

  const openAdd = () => {
    if (!isRig && !canViewAssignedRigs) return;
    if (!isRig && !selectedRigId) {
      setMessage("Select a rig first.");
      return;
    }
    setEditingLogId(null);
    setStartTime("");
    setFinishTime("");
    setTaskId("");
    setComments("");
    setMessage("");
    setAddOpen(true);
  };

  const openEditLog = (log) => {
    if (!isRig) return;
    setEditingLogId(log.id);
    setStartTime(log.start_time || "");
    setFinishTime(log.end_time || "");
    setTaskId(log.task_id || "");
    setComments(log.comments || "");
    setMessage("");
    setAddOpen(true);
  };

  const editTask = (task) => {
    setEditingTaskId(task.id);
    setTaskName(task.name || "");
    setTaskDescription(task.description || "");
    setManageMode("edit");
    setMessage("");
  };

  const openManage = () => {
    setManageMode("menu");
    setEditingTaskId(null);
    setTaskName("");
    setTaskDescription("");
    setMessage("");
    setManageOpen(true);
  };

  const timelineItems = useMemo(() => visibleLogs
    .map((log) => ({
      ...log,
      start: toMinutes(log.start_time),
      end: toMinutes(log.end_time),
      label: log.log_type || "Daily Log",
    }))
    .filter((x) => x.start != null && x.end != null && x.end > x.start)
    .sort((a, b) => a.start - b.start), [visibleLogs]);

  const timelineStart = timelineItems.length ? Math.floor(timelineItems[0].start / 60) * 60 : null;
  const timelineEnd = timelineItems.length ? Math.ceil(timelineItems[timelineItems.length - 1].end / 60) * 60 : null;
  const totalHours = timelineItems.reduce((sum, item) => sum + (item.end - item.start), 0) / 60;

  const timelineRows = useMemo(() => {
    if (!timelineItems.length) return [];
    const rows = [];
    timelineItems.forEach((item, index) => {
      rows.push({ type: "task", item });
      const next = timelineItems[index + 1];
      if (next && next.start - item.end >= 1) rows.push({ type: "gap", start: item.end, end: next.start });
    });
    return rows;
  }, [timelineItems]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Daily Log" subtitle={isRig ? "Single-day field timeline" : "Rig Daily Log viewer"} backTo="/" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-24">
        <div className="sticky top-0 z-30 -mx-4 px-4 py-3 mb-4 bg-slate-50/95 dark:bg-slate-950/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div>
              <h2 className="font-semibold text-lg">Daily Log</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{isRig ? "Today's timeline" : "View rig entries"}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canViewAssignedRigs && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="daily-log-rig" className="text-xs text-slate-500">Rig</Label>
                  <select id="daily-log-rig" value={selectedRigId} onChange={(e) => setSelectedRigId(e.target.value)} className="h-9 rounded-xl border bg-white dark:bg-slate-900 px-3 text-sm min-w-44">
                    {rigUsers.length === 0 ? <option value="">No assigned rigs</option> : rigUsers.map((rig) => <option key={rig.id} value={rig.id}>{userLabel(rig)}</option>)}
                  </select>
                </div>
              )}
              {canAdd && <Button className="rounded-xl" onClick={openAdd}>Add</Button>}
              {canManage && <Button variant="outline" className="rounded-xl" onClick={openManage}>Manage</Button>}
            </div>
          </div>
        </div>

        {!timelineItems.length ? (
          <div className="rounded-2xl border bg-white dark:bg-slate-900 dark:border-slate-800 p-10 text-center text-sm text-slate-500">No entries</div>
        ) : (
          <div className="rounded-2xl border bg-white dark:bg-slate-900 dark:border-slate-800 overflow-hidden">
            <div className="px-4 py-3 border-b dark:border-slate-800 text-sm font-medium">{formatHour(Math.floor(timelineStart / 60))} – {formatHour(Math.floor(timelineEnd / 60))}</div>
            <div className="divide-y dark:divide-slate-800">
              {timelineRows.map((row, index) => row.type === "gap" ? (
                <div key={`gap-${index}`} className="px-4 py-2 bg-red-50/70 dark:bg-red-950/20 text-red-700 dark:text-red-300 text-xs font-semibold flex items-center gap-2">
                  <span className="h-px flex-1 bg-red-500" /><span>Missing Data · {((row.end - row.start) / 60).toFixed(2)} h</span><span className="h-px flex-1 bg-red-500" />
                </div>
              ) : (
                <div key={row.item.id} className="w-full px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition">
                  <div className="flex items-start gap-3">
                    <button type="button" onClick={() => openEditLog(row.item)} disabled={!canEditLogs} className="min-w-0 flex-1 text-left disabled:cursor-default">
                      <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 items-start">
                        <div className="text-xs text-slate-500 pt-0.5 tabular-nums whitespace-nowrap">{row.item.start_time} – {row.item.end_time}</div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold leading-tight break-words">{row.item.label}</div>
                          {row.item.task_description && <div className="text-xs leading-tight text-slate-500 dark:text-slate-400 mt-0.5 break-words">{row.item.task_description}</div>}
                        </div>
                      </div>
                    </button>
                    {canEditLogs && <div className="flex shrink-0 gap-1">
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label="Edit Daily Log entry" title="Edit" onClick={() => openEditLog(row.item)}><Pencil className="h-4 w-4" /></Button>
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" aria-label="Delete Daily Log entry" title="Delete" onClick={() => deleteLog(row.item)}><Trash2 className="h-4 w-4" /></Button>
                    </div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-3 border-t dark:border-slate-800 text-sm font-semibold text-right">Total Hours: {totalHours.toFixed(2)}</div>
          </div>
        )}
      </main>

      {canAdd && <Dialog open={addOpen} onOpenChange={(open) => { if (!open) closeAdd(); }}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>{editingLogId ? "Edit Daily Log" : "Add Daily Log"}</DialogTitle><DialogDescription>Select a managed task and enter its times.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="min-w-0"><Label>Start Time</Label><TimeScroller value={startTime} onChange={setStartTime} className="mt-1" /></div>
              <div className="min-w-0"><Label>Finish Time</Label><TimeScroller value={finishTime} onChange={setFinishTime} className="mt-1" /></div>
            </div>
            <div><Label>Task</Label><TaskPicker tasks={tasks} value={taskId} onChange={setTaskId} />{selectedTask?.description && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{selectedTask.description}</div>}</div>
            <div><Label>Comments</Label><Textarea value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Add comments..." className="mt-1 min-h-[72px] rounded-xl" /></div>
            {hasOverlap && <p className="text-sm font-semibold text-red-600">Overlap detected. Separate tasks must not overlap by 1 minute or more.</p>}
            {message && <p className="text-sm text-red-600">{message}</p>}
            <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-xl" onClick={closeAdd}>Cancel</Button><Button className="rounded-xl" onClick={save}>Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>}

      {canManage && <Dialog open={manageOpen} onOpenChange={(open) => { setManageOpen(open); if (!open) { setManageMode("menu"); setEditingTaskId(null); setTaskName(""); setTaskDescription(""); setMessage(""); } }}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle>Manage Daily Log Tasks</DialogTitle><DialogDescription>{manageMode === "menu" ? "Choose an action for the shared task list." : manageMode === "create" ? "Create a task available to all users." : "Edit an existing task available to all users."}</DialogDescription></DialogHeader>
          {manageMode === "menu" ? <div className="grid grid-cols-2 gap-3"><Button className="h-20 rounded-xl" onClick={() => { setManageMode("create"); setEditingTaskId(null); setTaskName(""); setTaskDescription(""); setMessage(""); }}>Create</Button><Button variant="outline" className="h-20 rounded-xl" onClick={() => setManageMode("edit")}>Edit</Button><Button variant="ghost" className="col-span-2" onClick={() => setManageOpen(false)}>Close</Button></div> : <div className="space-y-3">
            {manageMode === "edit" && !editingTaskId && <div className="border rounded-xl p-3 space-y-2 max-h-64 overflow-auto">{tasks.length === 0 ? <p className="text-sm text-slate-500">No tasks have been created.</p> : tasks.map((t) => <div key={t.id} className="flex items-center justify-between rounded-lg border p-3"><div><div className="font-medium">{t.name}</div><div className="text-xs text-slate-500">{t.description}</div></div><Button size="sm" variant="outline" onClick={() => editTask(t)}>Edit</Button></div>)}</div>}
            {(manageMode === "create" || editingTaskId) && <><div><Label>Name</Label><Input value={taskName} onChange={(e) => setTaskName(e.target.value)} className="rounded-xl mt-1" /></div><div><Label>Description</Label><Textarea value={taskDescription} onChange={(e) => setTaskDescription(e.target.value)} className="rounded-xl mt-1" /></div>{message && <p className="text-sm text-red-600">{message}</p>}<div className="flex justify-end gap-2"><Button variant="outline" onClick={() => { setEditingTaskId(null); setTaskName(""); setTaskDescription(""); setMessage(""); setManageMode("menu"); }}>Back</Button><Button onClick={saveTask}>{editingTaskId ? "Save" : "Add"}</Button><Button variant="outline" onClick={() => setManageOpen(false)}>Close</Button></div></>}
          </div>}
        </DialogContent>
      </Dialog>}
    </div>
  );
}
